import { isArray } from "lodash";
import moment from "moment";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

import "sweetalert2/dist/sweetalert2.css";
import { HOST_PORT } from "../constants/config";
import AuthStorage from "../utils/authStorage";
// import "react-toastify/dist/ReactToastify.css";

export const isEmpty = (value) => {
	if (value === null || value === "null") {
		return true;
	}
	if (typeof value === "object") {
		return Object.keys(value).length === 0;
	}
	return (isArray(value) && value.length === 0) || value === undefined || value === "undefined" || value === null || value === "";
};

export const toaster = {
	error: (message, config = {}) => {
		if (!isEmpty(message?.message) || !isEmpty(message)) {
			toast.error(message, {
				position: "top-center",
				autoClose: 1500,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				style: {
					zIndex: 99999,
				},
				theme: "light",
				...config,
			});
		}
	},
	success: (message, config = {}) =>
		toast.success(message, {
			position: "top-center",
			autoClose: 1500,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: "light",
			// progressStyle: { background: '#F1F5F9' },
			// theme: 'colored',
			// style: { background: '#50C793' },
			...config,
		}),
	warning: (message, config = {}) =>
		toast.warning(message, {
			position: "top-right",
			autoClose: 1500,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: "light",
			...config,
		}),
	info: (message, config = {}) =>
		toast.info(message, {
			position: "top-right",
			autoClose: 1500,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: "light",
			...config,
		}),
};

export const sweetAlert = {
	delete: (message = "Are you sure you want to delete this record?", confirmButtonText = "Delete") => {
		return new Promise((resolve) => {
			Swal.fire({
				title: "Are you sure?",
				text: message,
				icon: "warning",
				showCancelButton: true,
				confirmButtonText,
				customClass: {
					container: "contained buttons",
					popup: "custom-popup",
					title: "custom-title",
					htmlContainer: "custom-html",
					confirmButton: "custom-confirm",
					cancelButton: "custom-cancel",
				},
			}).then((result) => {
				if (result.isConfirmed) {
					resolve(result);
				}
			});
		});
	},

	success: (message = "Your work has been saved") => {
		Swal.fire({
			position: "top-end",
			text: message,
			showConfirmButton: false,
			width: "300px",
			timer: 1500,
		});
	},

	deleted: (message = "Record deleted") => {
		Swal.fire("Deleted!", message, "success");
	},
};

export const formatDate = (date, format = "YYYY-MM-DD HH:mm:ss") => {
	return date ? moment(date, "YYYY-MM-DD HH:mm:ss").format(format) : "";
};

export const prepareSelectDropdown = (array, field1 = null, field2) => {
	if (isEmpty(array)) {
		return [];
	}
	return array
		.map((item) => {
			return {
				label: isEmpty(field1) ? item : item[field1],
				value: isEmpty(field2) ? item : item[field2],
			};
		})
		.filter((e) => !isEmpty(e.label));
};

export const GetFormattedAmount = (amount, withCrDr = false, abs = true) => {
	let formatedAmount = withCrDr ? "" : "";
	if (!Number.isNaN(amount)) {
		amount = Number(amount).toFixed(2);

		if (abs) {
			formatedAmount = new Intl.NumberFormat("en-IN", {
				style: "decimal",
				currency: "INR",
				minimumFractionDigits: 2,
			}).format(Math.abs(amount));
		} else {
			formatedAmount = new Intl.NumberFormat("en-IN", {
				style: "decimal",
				currency: "INR",
				minimumFractionDigits: 2,
			}).format(amount);
		}
	}

	if (withCrDr) {
		amount < 0 ? (formatedAmount += " Cr.") : (formatedAmount += " Dr.");
	}

	return formatedAmount;
};

export const checkFileType = (url) => {
	// Get the file extension from the URL
	const fileExtension = url.split(".").pop().toLowerCase();

	// List of common image file extensions
	const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg"];

	// List of common video file extensions
	const videoExtensions = ["mp4", "avi", "mov", "wmv", "mkv", "flv", "webm", "mpeg"];

	// Check if the file extension matches with an image extension
	if (imageExtensions.includes(fileExtension)) {
		return "image";
	}

	// Check if the file extension matches with a video extension
	if (videoExtensions.includes(fileExtension)) {
		return "video";
	}

	// If the file extension doesn't match either, it might be an unknown type or not supported
	return "unknown";
};

export function _bool(val) {
	return !isEmpty(val) && (val === true || val === "true") ? true : false;
}

export function ExportToExcel(apiData, fileName) {
	const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
	const fileExtension = ".xlsx";
	const ws = XLSX.utils.json_to_sheet(apiData);
	const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
	const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
	const data = new Blob([excelBuffer], { type: fileType });
	FileSaver.saveAs(data, fileName + fileExtension);
}

const getJobTypeLabel = (jobType) => {
	if (jobType === "video_shoot") return "VIDEO";
	if (jobType === "photo_shoot") return "PHOTO";
	return String(jobType || "").replaceAll("_", " ").toUpperCase();
};

const getGstAmount = (jobWork) =>
	(jobWork.sgst_amount || 0) + (jobWork.cgst_amount || 0) + (jobWork.igst_amount || 0);

const MONTHLY_REPORT_HEADERS = [
	"JOB NUMBER ",
	"DATE",
	"CLIENT NAME",
	"PRODUCT",
	"JOB TYPE",
	"WORK TYPE",
	"NOTE",
	"QTY",
	"ANGLE",
	"TOTAL QTY",
	"RATE",
	"AMOUNT",
	"DISCOUNT",
	"GST",
	"TOTAL AMOUNT",
	"TOTAL PRODUCT",
	"MASTER NOTE",
];

const MONTHLY_REPORT_COLUMN_WIDTHS = [12, 14, 30, 18, 12, 14, 24, 9, 9, 11, 11, 13, 11, 8, 14, 30, 26];
const NUMERIC_COLUMN_NUMBERS = new Set([8, 9, 10, 11, 12, 13, 14, 15]);
const WRAP_TEXT_COLUMN_NUMBERS = new Set([7, 16, 17]);

const REPORT_FONT = { name: "Calibri", size: 12 };
const HEADER_FONT = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
const GRAND_TOTAL_LABEL_FONT = { name: "Calibri", size: 12, bold: true };
const GRAND_TOTAL_VALUE_FONT = { name: "Calibri", size: 12, bold: true };

const HEADER_FILL = {
	type: "pattern",
	pattern: "solid",
	fgColor: { argb: "FF2F5597" },
};

const GRAND_TOTAL_ROW_FILL = {
	type: "pattern",
	pattern: "solid",
	fgColor: { argb: "FFE9ECEF" },
};

const DATA_ROW_FILL = {
	type: "pattern",
	pattern: "solid",
	fgColor: { argb: "FFF8F9FA" },
};

const TABLE_BORDER = {
	top: { style: "thin", color: { argb: "FFB4C6E7" } },
	left: { style: "thin", color: { argb: "FFB4C6E7" } },
	bottom: { style: "thin", color: { argb: "FFB4C6E7" } },
	right: { style: "thin", color: { argb: "FFB4C6E7" } },
};

const getCellAlignment = (columnNumber) => ({
	vertical: "middle",
	horizontal: NUMERIC_COLUMN_NUMBERS.has(columnNumber) ? "right" : "left",
	wrapText: WRAP_TEXT_COLUMN_NUMBERS.has(columnNumber),
});

const getWrappedRowHeight = (valuesByColumn = {}) => {
	const baseHeight = 24;
	let maxHeight = baseHeight;

	[16, 17].forEach((columnNumber) => {
		const text = valuesByColumn[columnNumber];
		if (!text) return;

		const columnWidth = MONTHLY_REPORT_COLUMN_WIDTHS[columnNumber - 1];
		const charsPerLine = Math.max(Math.floor(columnWidth * 1.1), 6);
		const lineCount = String(text)
			.split("\n")
			.reduce((count, line) => count + Math.max(1, Math.ceil(line.length / charsPerLine)), 0);

		maxHeight = Math.max(maxHeight, 20 + lineCount * 15);
	});

	return maxHeight;
};

const styleWorksheetColumns = (worksheet) => {
	MONTHLY_REPORT_COLUMN_WIDTHS.forEach((width, index) => {
		worksheet.getColumn(index + 1).width = width;
	});
};

const styleTableCell = (cell, columnNumber, { font = REPORT_FONT, fill = null } = {}) => {
	cell.font = font;
	cell.border = TABLE_BORDER;
	cell.alignment = getCellAlignment(columnNumber);

	if (fill) {
		cell.fill = fill;
	}
};

const styleHeaderRow = (row) => {
	row.height = 28;
	row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
		styleTableCell(cell, columnNumber, { font: HEADER_FONT, fill: HEADER_FILL });
		cell.alignment = {
			vertical: "middle",
			horizontal: "center",
			wrapText: true,
		};
	});
};

const styleDataRow = (row) => {
	row.height = 22;
	row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
		styleTableCell(cell, columnNumber);
	});
};

const styleGrandTotalRow = (row, wrappedValues = {}) => {
	row.height = getWrappedRowHeight(wrappedValues);
	row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
		const isLabelCell = columnNumber === 9 || columnNumber === 11;
		styleTableCell(cell, columnNumber, {
			font: isLabelCell ? GRAND_TOTAL_LABEL_FONT : GRAND_TOTAL_VALUE_FONT,
			fill: GRAND_TOTAL_ROW_FILL,
		});

		if (columnNumber === 3 || WRAP_TEXT_COLUMN_NUMBERS.has(columnNumber)) {
			cell.alignment = {
				vertical: "middle",
				horizontal: "left",
				wrapText: true,
			};
		}

		if (columnNumber === 3) {
			cell.font = GRAND_TOTAL_VALUE_FONT;
		}
	});
};

const addSpacerRow = (worksheet) => {
	const spacerRow = worksheet.addRow([]);
	spacerRow.height = 10;
	spacerRow.eachCell({ includeEmpty: true }, (cell) => {
		cell.fill = DATA_ROW_FILL;
	});
};

export async function ExportMonthlyJobWorkReportToExcel(jobWorks, fileName, { includeAmount = true } = {}) {
	const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
	const fileExtension = ".xlsx";

	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet("Sheet1", {
		properties: { defaultRowHeight: 22 },
		views: [{ state: "frozen", xSplit: 3, ySplit: 1, topLeftCell: "D2", activeCell: "D2" }],
	});

	styleWorksheetColumns(worksheet);

	const headerRow = worksheet.addRow(MONTHLY_REPORT_HEADERS);
	styleHeaderRow(headerRow);

	const sortedJobWorks = [...jobWorks].sort((a, b) => {
		const clientCompare = (a.client_name || "").localeCompare(b.client_name || "", undefined, { sensitivity: "base" });
		if (clientCompare !== 0) return clientCompare;
		return new Date(b.received_date).getTime() - new Date(a.received_date).getTime();
	});

	for (const jobWork of sortedJobWorks) {
		for (const detail of jobWork.job_work_details || []) {
			const dataRow = worksheet.addRow([
				jobWork.job_number,
				moment(jobWork.received_date).format("DD-MM-YYYY"),
				jobWork.client_name,
				detail.product_name,
				getJobTypeLabel(detail.job_type),
				(detail.work_type_name || "").toUpperCase(),
				detail.notes || "",
				detail.quantity,
				detail.angles,
				detail.total_qty,
				includeAmount ? detail.rate : "",
				includeAmount ? detail.amount : "",
				"",
				"",
				"",
				"",
				"",
			]);
			styleDataRow(dataRow);
		}

		const totalProductStr =
			(jobWork.product_list || [])
				.map((product) => `${product.quantity} ${product.product_name}`)
				.join(", ") + ((jobWork.product_list || []).length ? "," : "");

		const gstAmount = getGstAmount(jobWork);

		const grandTotalRow = worksheet.addRow([
			"",
			"",
			jobWork.client_name,
			"",
			"",
			"",
			"",
			"",
			" JOB QTY",
			jobWork.total_quantity,
			" JOB AMOUNT",
			includeAmount ? jobWork.total_amount : "",
			includeAmount ? jobWork.discount : "",
			includeAmount ? gstAmount : "",
			includeAmount ? jobWork.net_amount : "",
			totalProductStr,
			jobWork.notes || "",
		]);
		styleGrandTotalRow(grandTotalRow, {
			16: totalProductStr,
			17: jobWork.notes || "",
		});
		addSpacerRow(worksheet);
	}

	worksheet.autoFilter = {
		from: { row: 1, column: 3 },
		to: { row: 1, column: 3 },
	};

	const excelBuffer = await workbook.xlsx.writeBuffer();
	const data = new Blob([excelBuffer], { type: fileType });
	FileSaver.saveAs(data, fileName + fileExtension);
}

/**
 * Merge invoice logo into PDF payload (base64 required for @react-pdf in the browser).
 * @param {object} invoiceDetails - payload from GET /invoices/invoice_pdf/:id
 * @param {object} [settings] - payload from GET /app_settings/invoice (optional)
 */
export const withInvoiceLogoBase64 = async (invoiceDetails, settings) => {
	const mergedSettings = {
		...(invoiceDetails?.invoice_details || {}),
		...(settings || {}),
	};

	if (mergedSettings.logo_base64) {
		return { ...invoiceDetails, invoice_details: mergedSettings };
	}

	if (!mergedSettings.logo) {
		return invoiceDetails;
	}

	const url =
		mergedSettings.logo_url ||
		(mergedSettings.logo.startsWith("http")
			? mergedSettings.logo
			: `${HOST_PORT.replace(/\/$/, "")}${mergedSettings.logo}`);

	try {
		const token = AuthStorage.getToken();
		const res = await fetch(url, {
			headers: token ? { Authorization: `Bearer ${token}` } : {},
		});
		if (!res.ok) {
			return { ...invoiceDetails, invoice_details: mergedSettings };
		}
		const blob = await res.blob();
		const logo_base64 = await new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
		return {
			...invoiceDetails,
			invoice_details: { ...mergedSettings, logo_base64 },
		};
	} catch {
		return { ...invoiceDetails, invoice_details: mergedSettings };
	}
};
